// ============================================================================
// Weidner Oasis fork of the base oasis AmenitiesList.
//
// Delta vs the base: the paginated amenity columns were clipping the last
// visible items at the bottom. The base measured items-per-page against the
// list viewport *before* pagination controls mounted; once the controls
// appeared they stole vertical space and `overflow:hidden` chopped the
// bottom of the page. On stacked (mobile) layouts the same
// `flex:1` + `min-height:0` + `overflow:hidden` combo could also clip the
// auto-height list. This fork reserves pagination height when measuring,
// skips pagination below the stacked breakpoint, and lets the list size to
// content on mobile. All structure, tokens, slots, and the schema are
// otherwise unchanged from the base.
// ============================================================================
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableToken, useCollectionBinding, useContentToken } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
// Reuses the same gold diamond pattern that anchors the Footer,
// PropertyOverview and HomeNeighborhood sections — keeps the Amenities
// page feeling like part of the same brand family.
import footerPatternUrl from '@vibesites/theme-oasis/assets/footer-pattern.png';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

// ============================================================================
// Photo slider
// ============================================================================

const SLIDE_INTERVAL = 5000;

interface PhotoSliderProps {
  photos: GalleryPhoto[];
  heading: string;
}

function PhotoSlider({ photos, heading }: PhotoSliderProps) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const count = photos.length;

  const advance = useCallback((dir: 1 | -1) => {
    setCurrent(prev => (prev + dir + count) % count);
  }, [count]);

  useEffect(() => {
    if (count <= 1 || paused) return;
    timerRef.current = setInterval(() => advance(1), SLIDE_INTERVAL);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [count, paused, advance]);

  if (count === 0) return null;

  return (
    <div
      className="amenities-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {photos.map((photo, i) => (
        <VibesitesImage
          key={photo.image || i}
          url={photo.image!}
          alt={photo.alt_text || photo.title || heading}
          className={`amenities-slider-img${i === current ? ' amenities-slider-img--active' : ''}`}
          sizes="(min-width: 769px) 50vw, 100vw"
          priority
        />
      ))}

      {count > 1 && (
        <>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--prev"
            onClick={() => advance(-1)}
            aria-label="Previous photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <button
            className="amenities-slider-arrow amenities-slider-arrow--next"
            onClick={() => advance(1)}
            aria-label="Next photo"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>

          <div className="amenities-slider-dots">
            {photos.map((_, i) => (
              <button
                key={i}
                className={`amenities-slider-dot${i === current ? ' amenities-slider-dot--active' : ''}`}
                onClick={() => setCurrent(i)}
                aria-label={`Photo ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Paginated amenities list
//
// The image panel has a fixed height. The list panel inherits that same row
// height. If the list of items would render taller than the visible area,
// we slice it into pages and show prev/next controls.
//
// Approach: a hidden "measurement" copy of the full list is rendered behind
// the visible list with the same width/layout but with no height constraint.
// We binary-search the largest item prefix whose scrollHeight fits in the
// outer list-area height minus a reservation for the pagination controls
// (so mounting the controls can't shrink the viewport and clip the page).
// Below the stacked breakpoint, pagination is skipped and the full list
// scrolls with the page.
// ============================================================================

interface PaginatedAmenitiesListProps {
  items: Amenity[];
}

// Approximate height of `.amenities-pagination` (button row + padding).
// Used when controls aren't mounted yet so the first measure still reserves
// room for them — otherwise items-per-page is computed against a taller
// viewport than the page will actually have once controls appear.
const PAGINATION_HEIGHT_FALLBACK = 48;

function PaginatedAmenitiesList({ items }: PaginatedAmenitiesListProps) {
  const [itemsPerPage, setItemsPerPage] = useState<number>(items.length || 1);
  const [page, setPage] = useState(0);

  const measureRef = useRef<HTMLUListElement>(null);
  // Outer list area — height is stable whether or not pagination is mounted.
  const areaRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (items.length === 0) {
      setItemsPerPage(1);
      return;
    }

    const measure = () => {
      const list = measureRef.current;
      const area = areaRef.current;
      if (!list || !area) return;

      // Stacked layout: the row is auto-height and the page scrolls — show
      // every item, no pagination (matches the CSS intent below 768px).
      if (window.matchMedia('(max-width: 768px)').matches) {
        setItemsPerPage(items.length);
        return;
      }

      const children = Array.from(list.children) as HTMLElement[];
      // Ensure every item is visible for the natural-height check / search.
      for (const child of children) child.style.display = '';

      const naturalH = list.scrollHeight;
      const areaH = area.clientHeight;
      if (areaH <= 0) return;

      if (naturalH <= areaH + 2) {
        setItemsPerPage(items.length);
        return;
      }

      // Content overflows: pagination will mount and claim vertical space.
      // Reserve that space up front (prefer the live control height once
      // mounted; otherwise the fallback) so the last items on a page aren't
      // clipped by the list viewport's overflow:hidden.
      const paginationH =
        paginationRef.current?.offsetHeight || PAGINATION_HEIGHT_FALLBACK;
      const availH = Math.max(0, areaH - paginationH - 2);

      // Binary-search the largest prefix of items whose balanced multi-column
      // height still fits in the reserved viewport. More reliable than a
      // height-ratio approximation when item heights vary.
      let lo = 1;
      let hi = items.length;
      let best = 1;
      while (lo <= hi) {
        const mid = Math.floor((lo + hi) / 2);
        for (let i = 0; i < children.length; i++) {
          children[i].style.display = i < mid ? '' : 'none';
        }
        if (list.scrollHeight <= availH) {
          best = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      for (const child of children) child.style.display = '';
      setItemsPerPage(Math.max(1, best));
    };

    measure();
    setPage(0);

    const cleanups: Array<() => void> = [];

    if (typeof ResizeObserver !== 'undefined' && areaRef.current) {
      const ro = new ResizeObserver(() => measure());
      ro.observe(areaRef.current);
      cleanups.push(() => ro.disconnect());
    }

    // Remeasure when crossing the stacked ↔ side-by-side breakpoint so we
    // enable/disable pagination as the layout mode changes.
    const mql = window.matchMedia('(max-width: 768px)');
    const onBreakpoint = () => measure();
    mql.addEventListener('change', onBreakpoint);
    cleanups.push(() => mql.removeEventListener('change', onBreakpoint));

    return () => { cleanups.forEach((fn) => fn()); };
  }, [items.length]);

  const safeItemsPerPage = Math.max(1, itemsPerPage);
  const totalPages = Math.max(1, Math.ceil(items.length / safeItemsPerPage));
  const safePage = Math.min(page, totalPages - 1);
  const currentItems = items.slice(
    safePage * safeItemsPerPage,
    (safePage + 1) * safeItemsPerPage
  );

  return (
    <div ref={areaRef} className="amenities-list-area">
      <div className="amenities-list-area-inner">
        {/* Hidden measurement pass: full list rendered with no height cap so
            we can read its natural scrollHeight. */}
        <ul
          ref={measureRef}
          className="amenities-col-list amenities-col-list--measure"
          aria-hidden="true"
        >
          {items.map((item, i) => (
            <li key={`m-${i}`} className="amenities-col-item">
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>

        {/* Visible (paginated) list */}
        <ul className="amenities-col-list">
          {currentItems.map((item, i) => (
            <li key={`p${safePage}-${i}`} className="amenities-col-item">
              <span>{item.title || ''}</span>
            </li>
          ))}
        </ul>
      </div>

      {totalPages > 1 && (
        <div ref={paginationRef} className="amenities-pagination">
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.max(0, safePage - 1))}
            disabled={safePage === 0}
            aria-label="Previous amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
          </button>
          <span className="amenities-pagination-info">
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            className="amenities-pagination-btn"
            onClick={() => setPage(Math.min(totalPages - 1, safePage + 1))}
            disabled={safePage === totalPages - 1}
            aria-label="Next amenities page"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6" /></svg>
          </button>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Column component
// ============================================================================

interface AmenityColumnProps {
  headingNode: ReactNode;
  headingText: string;
  items: Amenity[];
  photos: GalleryPhoto[];
  imagePosition: 'left' | 'right';
}

function AmenityColumn({ headingNode, headingText, items, photos, imagePosition }: AmenityColumnProps) {
  const colRef = useScrollReveal<HTMLDivElement>();
  const hasImage = photos.length > 0;

  const contentPanel = (
    <div className="amenities-row-content">
      {headingNode}
      <PaginatedAmenitiesList items={items} />
    </div>
  );

  const imagePanel = hasImage ? (
    <div className="amenities-row-image">
      {/* Gold diamond pattern fills the gallery column and shows around the
          photo as a decorative mat — same motif used across the site. */}
      <div className="amenities-row-pattern" aria-hidden="true" />
      <div className="amenities-row-image-inner">
        <PhotoSlider photos={photos} heading={headingText} />
      </div>
    </div>
  ) : null;

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenities-row amenities-row--img-${imagePosition}${hasImage ? '' : ' amenities-row--no-image'}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AmenitiesList() {
  const communityHeading = useContentToken('amenitieslist.communityHeading');
  const apartmentHeading = useContentToken('amenitieslist.apartmentHeading');

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 8,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 8,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="amenities-section pt-12 pb-6 sm:pt-16 sm:pb-8 md:pt-20 md:pb-10 lg:py-32 bg-[var(--vibe-color-background)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {hasContent ? (
          <div className="amenities-stack">
            {communityItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <EditableToken
                    path="amenitieslist.communityHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {communityHeading || 'Community Amenities'}
                  </EditableToken>
                }
                headingText={communityHeading || 'Community Amenities'}
                items={communityItems}
                photos={communityPhotos}
                imagePosition="right"
              />
            )}
            {apartmentItems.length > 0 && (
              <AmenityColumn
                headingNode={
                  <EditableToken
                    path="amenitieslist.apartmentHeading"
                    as="h3"
                    className="amenities-col-heading"
                  >
                    {apartmentHeading || 'Interior Luxuries'}
                  </EditableToken>
                }
                headingText={apartmentHeading || 'Interior Luxuries'}
                items={apartmentItems}
                photos={apartmentPhotos}
                imagePosition="left"
              />
            )}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        /* Clip any horizontal pattern bleed so it can't trigger a page-
           wide horizontal scrollbar on smaller viewports. Vertical bleed
           is contained by the section's generous py padding. */
        .amenities-section {
          overflow: hidden;
        }

        /* Stacked rows container — generous gap so each gallery row reads
           as its own moment on the page and the pattern bleed above/below
           never crowds the adjacent row. The gap tightens at narrower
           breakpoints because the pattern bleed also shrinks (see below)
           and 12rem of empty space starts to feel like dead air on a
           phone-sized viewport. */
        .amenities-stack {
          display: flex;
          flex-direction: column;
          gap: 12rem;
        }
        @media (max-width: 900px) {
          .amenities-stack {
            gap: 4rem;
          }
        }
        @media (max-width: 768px) {
          .amenities-stack {
            gap: 2.5rem;
          }
        }

        /* Each row: side-by-side image + content with a fixed height so the
           image gallery stays in a wide landscape aspect. Overflow is
           visible so the square pattern element behind the gallery can
           extend slightly above and below the row bounds. */
        .amenities-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          height: 380px;
          overflow: visible;
        }
        .amenities-row--no-image {
          grid-template-columns: 1fr;
          height: auto;
          min-height: 220px;
        }

        /* Content panel */
        .amenities-row-content {
          display: flex;
          flex-direction: column;
          padding: 0;
          min-height: 0;
        }

        /* Image panel. The gold diamond pattern is a separate square-ish
           block sized smaller than the landscape photo: it bleeds above
           and below the image so it reads as a vertical accent strip
           rather than a backdrop covering the entire gallery. */
        .amenities-row-image {
          position: relative;
          height: 100%;
        }
        .amenities-row-pattern {
          position: absolute;
          /* Vertical bleed above/below the landscape image. Horizontal
             anchor is set per row variant below — the pattern bleeds
             past the OUTSIDE edge of each gallery (the page-edge side)
             and reaches MOST of the way toward the INSIDE edge (the
             list-facing side), leaving a small visible gap before the
             list begins. */
          top: -60px;
          bottom: -60px;
          width: min(640px, 92%);
          background-image: url(${footerPatternUrl});
          background-repeat: repeat;
          background-size: 320px auto;
          background-position: center center;
          opacity: 0.95;
          pointer-events: none;
          z-index: 0;
        }
        /* Interior Luxuries row: image on the left, so the page-edge
           (outside) side is the LEFT — pattern bleeds left, stops short
           of the image's right edge facing the list. */
        .amenities-row--img-left .amenities-row-pattern {
          left: -60px;
        }
        /* Community Amenities row: image on the right, so the outside
           side is the RIGHT — pattern bleeds right. */
        .amenities-row--img-right .amenities-row-pattern {
          right: -60px;
        }
        .amenities-row-image-inner {
          position: relative;
          z-index: 1;
          height: 100%;
          /* Photo runs nearly full-width of the column so it overlaps the
             pattern horizontally — pattern only peeks above and below. */
          padding: 0;
          box-sizing: border-box;
        }
        .amenities-row-image-inner .amenities-slider {
          height: 100%;
        }

        /* Tablet: keep side-by-side layout, just slightly shorter row.
           Switch the pattern from a one-sided outside bleed to an
           all-around frame: the pattern fills the entire image column
           (with a small outward bleed on all four edges) and the photo is
           inset with padding so a strip of pattern shows around it on
           every side. */
        @media (max-width: 900px) {
          .amenities-row {
            height: 320px;
          }
          .amenities-row-image-inner {
            padding: 16px 22px;
          }
          /* Reset the desktop's outside-only horizontal anchor so the
             pattern can bleed equally on both sides. */
          .amenities-row--img-left .amenities-row-pattern,
          .amenities-row--img-right .amenities-row-pattern {
            left: -12px;
            right: -12px;
          }
          .amenities-row-pattern {
            top: -12px;
            bottom: -12px;
            width: auto;
            background-size: 190px auto;
          }
        }

        /* Mobile (stacked): image on top, content below. The content row
           is auto-sized instead of a fixed 360px — on a stacked layout
           there's no benefit to forcing pagination, the page just scrolls.
           The image is also taller (was 220px) so the landscape photo
           doesn't feel cramped. The pattern now frames the photo on ALL
           four sides: it fills the entire image cell (with a small
           outward bleed) and the photo is inset with padding so a strip
           of pattern shows around it. The row-gap adds breathing room
           between the gallery and the heading below it. */
        @media (max-width: 768px) {
          .amenities-row {
            grid-template-columns: 1fr;
            grid-template-rows: 280px auto;
            height: auto;
            row-gap: 1.75rem;
          }
          .amenities-row--no-image {
            grid-template-rows: auto;
            min-height: 220px;
          }
          /* Image on top regardless of the desktop stagger. */
          .amenities-row--img-right > .amenities-row-content {
            order: 2;
          }
          .amenities-row--img-right > .amenities-row-image {
            order: 1;
          }
          .amenities-row-image {
            height: 280px;
          }
          /* Auto-height stacked layout: size the list to its content and
             drop overflow clipping. The desktop flex:1 + min-height:0 +
             overflow:hidden combo is for fixed-height pagination; here it
             can collapse/clip the list at the bottom. */
          .amenities-list-area {
            flex: none;
            min-height: auto;
          }
          .amenities-list-area-inner {
            flex: none;
            overflow: visible;
            min-height: auto;
          }
          /* Photo is inset on all sides so the pattern shows around it as
             a frame. Slightly more horizontal inset than vertical so the
             pattern strip on the wide sides matches what's visible above
             and below the landscape photo. */
          .amenities-row-image-inner {
            padding: 18px 28px;
          }
          /* Replace the desktop's one-sided horizontal anchor and the
             previous translateX-centered narrow pattern with a four-edge
             bleed that fills the image cell. */
          .amenities-row--img-left .amenities-row-pattern,
          .amenities-row--img-right .amenities-row-pattern {
            left: -10px;
            right: -10px;
            transform: none;
          }
          .amenities-row-pattern {
            top: -10px;
            bottom: -10px;
            width: auto;
            background-size: 160px auto;
            opacity: 0.9;
          }
        }

        @media (max-width: 600px) {
          .amenities-row {
            grid-template-rows: 220px auto;
            row-gap: 1.25rem;
          }
          .amenities-row-image {
            height: 220px;
          }
          .amenities-row-image-inner {
            padding: 14px 20px;
          }
          .amenities-row-pattern {
            top: -8px;
            bottom: -8px;
            background-size: 135px auto;
          }
          .amenities-row--img-left .amenities-row-pattern,
          .amenities-row--img-right .amenities-row-pattern {
            left: -8px;
            right: -8px;
          }
        }

        /* Photo slider */
        .amenities-slider {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          flex-shrink: 0;
        }
        .amenities-slider-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .amenities-slider-img--active {
          opacity: 1;
        }

        .amenities-slider-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: none;
          background: rgba(0, 0, 0, 0.35);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.3s, background 0.3s;
          z-index: 2;
        }
        .amenities-slider:hover .amenities-slider-arrow {
          opacity: 1;
        }
        .amenities-slider-arrow:hover {
          background: rgba(0, 0, 0, 0.55);
        }
        .amenities-slider-arrow--prev { left: 10px; }
        .amenities-slider-arrow--next { right: 10px; }

        .amenities-slider-dots {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: 6px;
          z-index: 2;
        }
        .amenities-slider-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          border: 1.5px solid rgba(255, 255, 255, 0.7);
          background: transparent;
          padding: 0;
          cursor: pointer;
          transition: background 0.25s, border-color 0.25s, transform 0.25s;
        }
        .amenities-slider-dot:hover {
          transform: scale(1.25);
          border-color: white;
        }
        .amenities-slider-dot--active {
          background: white;
          border-color: white;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenities-slider-img {
            transition: none;
          }
        }

        .amenities-col-heading {
          font-family: var(--vibe-font-body);
          font-size: 1.4rem;
          font-weight: 700;
          color: var(--vibe-color-secondary);
          text-align: left;
          text-transform: uppercase;
          padding: 1.5rem 2rem 0.25rem;
          margin: 0;
          letter-spacing: 0.08em;
        }

        @media (max-width: 900px) {
          .amenities-col-heading {
            font-size: 1.1rem;
            padding: 1rem 1rem 0.25rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-heading {
            font-size: 0.95rem;
            padding: 0.75rem 0.75rem 0.25rem;
            letter-spacing: 0.06em;
            text-align: center;
          }
        }

        /* List area takes the remaining vertical space inside the row */
        .amenities-list-area {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
        }
        .amenities-list-area-inner {
          flex: 1;
          position: relative;
          overflow: hidden;
          min-height: 0;
        }

        .amenities-col-list {
          list-style: none;
          padding: 0.75rem 2rem 0.75rem;
          margin: 0;
          /* Multi-column layout (not grid): each column flows independently
             so a multi-line item never inflates a paired cell in the other
             column. Overflow detection still works off scrollHeight. */
          column-count: 2;
          column-gap: 1.5rem;
        }
        /* Hidden measurement copy: full list, no height cap. Anchored to the
           top/left/right of the container (matching the visible list's width)
           but with bottom:auto so the box grows to its natural content
           height. This is critical: with column-count:2, a height-constrained
           multicol box overflows HORIZONTALLY into extra columns instead of
           growing taller, so scrollHeight would never exceed the visible
           height and the overflow check would wrongly conclude everything
           fits — leaving the real list clipped with no pagination. */
        .amenities-col-list--measure {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: auto;
          visibility: hidden;
          pointer-events: none;
          height: auto;
        }

        /* Tablet: keep two columns, tighten spacing. Bottom padding is
           dropped to near-zero so the list doesn't add extra dead space
           below itself — the inter-row stack gap already separates rows. */
        @media (max-width: 900px) {
          .amenities-col-list {
            padding: 0.5rem 1rem 0.1rem;
            column-gap: 0.75rem;
          }
        }
        /* Mobile: keep two columns side-by-side, even tighter. */
        @media (max-width: 600px) {
          .amenities-col-list {
            padding: 0.4rem 0.75rem 0;
            column-gap: 0.5rem;
          }
        }

        .amenities-col-item {
          display: flex;
          align-items: flex-start;
          /* Prevent a wrapped item from being split across the column break. */
          break-inside: avoid;
          padding: 0.3rem 0;
          font-family: var(--vibe-font-body);
          font-size: 0.88rem;
          color: var(--vibe-color-text);
          line-height: 1.5;
        }

        @media (max-width: 900px) {
          .amenities-col-item {
            font-size: 0.8rem;
            padding: 0.2rem 0;
          }
        }
        @media (max-width: 600px) {
          .amenities-col-item {
            font-size: 0.72rem;
            padding: 0.15rem 0;
            line-height: 1.35;
          }
        }

        /* Pagination controls — only rendered when list overflows */
        .amenities-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          padding: 0.25rem 2rem 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          color: var(--vibe-color-text);
        }
        @media (max-width: 900px) {
          .amenities-pagination {
            padding: 0.25rem 1rem 0.75rem;
            gap: 0.4rem;
            font-size: 0.78rem;
          }
        }
        @media (max-width: 600px) {
          .amenities-pagination {
            padding: 0.25rem 0.75rem 0.6rem;
            gap: 0.35rem;
            font-size: 0.72rem;
          }
          .amenities-pagination-btn {
            width: 24px;
            height: 24px;
          }
          .amenities-pagination-info {
            min-width: 2.5rem;
          }
        }
        .amenities-pagination-btn {
          width: 28px;
          height: 28px;
          padding: 0;
          border-radius: 50%;
          border: 1px solid rgba(0, 0, 0, 0.15);
          background: white;
          color: var(--vibe-color-text);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, border-color 0.2s, color 0.2s, opacity 0.2s;
        }
        .amenities-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-primary);
          border-color: var(--vibe-color-primary);
          color: white;
        }
        .amenities-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }
        .amenities-pagination-info {
          min-width: 3rem;
          text-align: center;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'text',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'text',
      defaultValue: 'Interior Luxuries',
    },
  },
  collectionBindings: {
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
    },
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
    },
  },
};

(AmenitiesList as any).contentSchema = contentSchema;

export default AmenitiesList;
